import { useEffect, useRef, useState } from 'react';
import { Paperclip, Upload, FileText, Trash2, Download, Loader2, Sparkles } from 'lucide-react';
import { attachmentsApi, formatBytes, ORG_CATEGORIES, type Attachment, type AttachmentOwner } from '../api/attachments';
import { fmtTime } from '../lib/format';
import { useAuth } from '../lib/auth';
import { api } from '../api/client';

interface Props {
  ownerType: AttachmentOwner;
  ownerId: string;
  /** Показать селект «Категория» при загрузке. Можно передать кастомный список через `categories`. */
  withCategories?: boolean;
  /** Кастомные категории. Если не задано — ORG_CATEGORIES. */
  categories?: readonly string[];
}

export function Attachments({ ownerType, ownerId, withCategories, categories }: Props) {
  const cats = categories ?? ORG_CATEGORIES;
  const { user } = useAuth();
  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [category, setCategory] = useState(withCategories ? cats[0] : '');
  const inputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try { setItems(await attachmentsApi.list(ownerType, ownerId)); }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Ошибка'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [ownerType, ownerId]);

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true); setErr(null);
    try {
      for (const f of Array.from(files)) {
        await attachmentsApi.upload(ownerType, ownerId, f, category || undefined);
      }
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка загрузки');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    upload(e.dataTransfer.files);
  };

  const canDeleteOwn = (a: Attachment) =>
    user?.type === 'staff' || a.uploaded_by_id === user?.id;

  return (
    <div>
      {/* Список */}
      {loading ? (
        <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-500 py-3 text-center">Файлов нет</div>
      ) : (
        <ul className="space-y-1.5 mb-3">
          {items.map(a => (
            <li key={a.id} className="flex items-center gap-2 bg-slate-950/60 border border-slate-800 rounded px-2.5 py-1.5">
              <FileText size={13} className="text-blue-400 shrink-0" />
              <button onClick={() => attachmentsApi.download(a)}
                className="flex-1 text-left text-sm text-slate-200 hover:text-blue-400 truncate">
                {a.filename}
              </button>
              {a.category && <span className="text-[10px] text-slate-500">{a.category}</span>}
              <span className="text-[10px] text-slate-500">{formatBytes(a.size_bytes)}</span>
              <span className="text-[10px] text-slate-600 hidden md:inline">{fmtTime(a.created_at)}</span>
              <button onClick={() => attachmentsApi.download(a)} title="Скачать"
                className="text-slate-500 hover:text-blue-400 p-0.5"><Download size={11} /></button>
              {/* Распарсить анкету — только для PDF в Org-карточке (v0.9.60 #9.61) */}
              {ownerType === 'organization' &&
               ((a.content_type || '').includes('pdf') || a.filename.toLowerCase().endsWith('.pdf')) && (
                <button onClick={async () => {
                  try {
                    const { data } = await api.post(
                      `/api/v1/organizations/${ownerId}/attachments/${a.id}/parse-anketa`,
                      { apply: false });
                    const s = data.suggested || {};
                    const sm = data.summary || {};
                    const lines = [
                      `Извлечено из PDF: текстовых полей ${sm.text_fields_total}, чекбоксов ${sm.checked_boxes_total}`,
                      '',
                      `Контакт: ${s.contact_person || '—'}`,
                      `Email:   ${s.contact_email || '—'}`,
                      `Телефон: ${s.contact_phone || '—'}`,
                      `Дата:    ${s.fill_date || '—'}`,
                      `Длительность: ${s.duration_days ? s.duration_days + ' дней' : '—'}`,
                      `Объём:   ${s.data_volume || '—'}`,
                    ];
                    if (confirm(lines.join('\n') + '\n\nПрименить (только пустые поля)?')) {
                      const r = await api.post(
                        `/api/v1/organizations/${ownerId}/attachments/${a.id}/parse-anketa`,
                        { apply: true });
                      const applied = r.data.applied || {};
                      alert(`Применено: ${Object.keys(applied).join(', ') || '— ничего нового —'}`);
                      load();
                    }
                  } catch (e: any) {
                    alert('Парсинг не удался: ' + (e?.response?.data?.detail || e?.message || 'ошибка'));
                  }
                }} title="Распарсить PDF-анкету и заполнить карточку"
                  className="text-slate-500 hover:text-amber-300 p-0.5">
                  <Sparkles size={11} />
                </button>
              )}
              {canDeleteOwn(a) && (
                <button onClick={async () => {
                  if (window.confirm(`Удалить «${a.filename}»?`)) {
                    await attachmentsApi.remove(a.id); load();
                  }
                }} title="Удалить" className="text-slate-500 hover:text-red-400 p-0.5">
                  <Trash2 size={11} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Upload */}
      <div onDragOver={e => e.preventDefault()} onDrop={onDrop}
        className="border-2 border-dashed border-slate-700 rounded-lg p-3 text-center hover:border-blue-500 transition">
        <input ref={inputRef} type="file" multiple onChange={e => upload(e.target.files)} className="hidden" />
        <button onClick={() => inputRef.current?.click()} disabled={uploading}
          className="text-xs text-slate-400 hover:text-blue-400 inline-flex items-center gap-2">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Загружаю…' : 'Нажми или перетащи файл'}
        </button>
        {withCategories && (
          <div className="mt-2">
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="bg-slate-950 border border-slate-700 rounded px-2 py-0.5 text-[11px] text-slate-300 focus:border-blue-500 outline-none">
              {cats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
      </div>

      {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-xs mt-2">{err}</div>}
    </div>
  );
}

interface CardProps extends Props {
  /** Заголовок секции — по умолчанию «Документы / вложения». */
  title?: string;
}

export function AttachmentsCard({ ownerType, ownerId, withCategories, categories, title }: CardProps) {
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 font-bold text-white flex items-center gap-2">
        <Paperclip size={14} className="text-blue-400" /> {title ?? 'Документы / вложения'}
      </header>
      <div className="p-3">
        <Attachments ownerType={ownerType} ownerId={ownerId} withCategories={withCategories} categories={categories} />
      </div>
    </section>
  );
}
